import type { PipelineMetrics } from "@cdt/shared";
import { Activity, AlertTriangle, Zap } from "lucide-react";
import type { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface PipelineMetricsCardProps {
  pipeline: PipelineMetrics;
}

function fmt(value: number | null, unit: string): string {
  if (value === null) return "—";
  if (unit === "ms") {
    return value >= 1000 ? `${(value / 1000).toFixed(1)}s` : `${value}ms`;
  }
  if (unit === "%") return `${value.toFixed(1)}%`;
  return String(value);
}

interface TileProps {
  label: string;
  value: string;
  sub?: string;
  alert?: boolean;
}

function Tile({ label, value, sub, alert }: TileProps) {
  return (
    <div className="flex min-w-[84px] flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          "text-xl font-semibold tabular-nums",
          alert ? "text-amber-500" : "text-slate-900",
        )}
      >
        {value}
      </span>
      {sub && <span className="text-[11px] text-muted-foreground">{sub}</span>}
    </div>
  );
}

function Group({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-100/80 bg-white/40 p-4">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </span>
      <div className="flex gap-5">{children}</div>
    </div>
  );
}

export function PipelineMetricsCard({ pipeline }: PipelineMetricsCardProps) {
  const noData =
    pipeline.stage1.callCount === 0 && pipeline.cycleDurationMs.samples === 0;

  const lastCycleAlert =
    pipeline.cycleDurationMs.last !== null &&
    pipeline.cycleDurationMs.last > 300_000;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-100">
            <Zap className="h-4 w-4" />
          </span>
          Metriche pipeline AI
        </CardTitle>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {lastCycleAlert && (
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
          )}
          <Activity className="h-3.5 w-3.5" />
          <span>
            Finestra {pipeline.windowMinutes} min
            {pipeline.cycleDurationMs.samples > 0 &&
              ` · ${pipeline.cycleDurationMs.samples} cicli`}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {noData ? (
          <p className="text-sm text-muted-foreground">
            Nessun dato — attesa del primo ciclo di processing.
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-3">
            <Group title="Ciclo">
              <Tile
                label="Ultimo"
                value={fmt(pipeline.cycleDurationMs.last, "ms")}
                alert={lastCycleAlert}
              />
              <Tile label="Media" value={fmt(pipeline.cycleDurationMs.avg, "ms")} />
            </Group>

            <Group
              title={
                <>
                  Stage 1 — classify
                  {pipeline.stage1.callCount > 0 && (
                    <span className="ml-1 font-normal normal-case text-muted-foreground">
                      ({pipeline.stage1.callCount} chiamate)
                    </span>
                  )}
                </>
              }
            >
              <Tile label="Media" value={fmt(pipeline.stage1.avgMs, "ms")} />
              <Tile label="p95" value={fmt(pipeline.stage1.p95Ms, "ms")} />
            </Group>

            <Group title="Stage 2 — extract">
              <Tile label="Media" value={fmt(pipeline.stage2.avgMs, "ms")} />
              <Tile label="p95" value={fmt(pipeline.stage2.p95Ms, "ms")} />
              <Tile
                label="Hit rate"
                value={fmt(pipeline.stage2.hitRatePct, "%")}
              />
            </Group>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
